import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Layout from "../components/layout";
import {
  fetchContract,
  fetchContractEvents,
  fetchContractCalls,
  fetchContractTransactions,
} from "../services/contracts";
// import ContractInteraction from "../components/contracts/contractInteraction"; // COMMENTED OUT
import ContractEvents from "../components/contracts/contractEvents";
import ContractCalls from "../components/contracts/contractCalls";
import TokenHolders from "../components/contracts/tokenHolders";
import useChainSettings from "../utils/hooks/chain/useChainSettings";
import LoadingSpinner from "../components/loadingSpinner";
import { formatAddress, formatBalance } from "../utils/format";

export default function Contract() {
  const { address } = useParams();
  const [contract, setContract] = useState(null);
  const [events, setEvents] = useState([]);
  const [calls, setCalls] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("transactions");
  const [actualAbi, setActualAbi] = useState(null); // Track the actual ABI being used
  const { decimals, symbol } = useChainSettings();

  useEffect(() => {
    if (!address) return;

    setLoading(true);
    Promise.all([
      fetchContract(address),
      fetchContractEvents(address),
      fetchContractCalls(address),
      fetchContractTransactions(address),
    ])
      .then(([contractData, eventsData, callsData, transactionsData]) => {
        setContract(contractData);
        setEvents(
          eventsData?.items || (Array.isArray(eventsData) ? eventsData : []),
        );
        setCalls(
          callsData?.items || (Array.isArray(callsData) ? callsData : []),
        );
        setTransactions(transactionsData?.items || []);
      })
      .catch((error) => {
        console.error("Failed to fetch contract data:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [address]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex-1 bg-gray-50 p-6">
          <LoadingSpinner />
        </div>
      </Layout>
    );
  }

  if (!contract) {
    return (
      <Layout>
        <div className="flex-1 bg-gray-50 p-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h1 className="text-2xl font-semibold text-gray-900">
              Contract Not Found
            </h1>
            <p className="mt-2 text-gray-600">
              The contract address {address} could not be found.
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  const tabs = [
    { id: "transactions", label: "Transactions" },
    { id: "events", label: "Events" },
    // { id: "readwrite", label: "Read/Write" }, // COMMENTED OUT
    { id: "holders", label: "Token Holders" },
  ];

  return (
    <Layout>
      <div className="flex-1 bg-gray-50">
        {/* Header Section */}
        <div
          className="bg-white px-6 py-4 border-b border-gray-200"
          style={{
            backgroundColor: "white",
            padding: "16px 24px",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <div
            className="flex items-center justify-between mb-4"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "16px",
            }}
          >
            <h1
              className="text-2xl font-semibold text-gray-900"
              style={{
                fontSize: "24px",
                fontWeight: "600",
                color: "#111827",
              }}
            >
              Contract
            </h1>
            <button
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
              style={{
                padding: "8px 16px",
                backgroundColor: "#f3f4f6",
                color: "#374151",
                borderRadius: "8px",
                border: "none",
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
                transition: "background-color 0.2s ease",
                display: "flex",
                alignItems: "center",
              }}
              onMouseEnter={(e) => (e.target.style.backgroundColor = "#e5e7eb")}
              onMouseLeave={(e) => (e.target.style.backgroundColor = "#f3f4f6")}
            >
              <span className="mr-2" style={{ marginRight: "8px" }}>
                ⚙️
              </span>
              API
            </button>
          </div>

          <div
            className="flex items-center space-x-3"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <span
              className="font-mono text-lg text-gray-900"
              style={{
                fontFamily: "Overpass Mono, monospace",
                fontSize: "18px",
                color: "#111827",
              }}
            >
              {address}
            </span>
            <button
              onClick={() => copyToClipboard(address)}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-all"
              title="Copy address"
              style={{
                padding: "6px",
                color: "#9ca3af",
                backgroundColor: "transparent",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.target.style.color = "#6b7280";
                e.target.style.backgroundColor = "#f3f4f6";
              }}
              onMouseLeave={(e) => {
                e.target.style.color = "#9ca3af";
                e.target.style.backgroundColor = "transparent";
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.333 5.333h-6c-.735 0-1.333.598-1.333 1.334v6c0 .735.598 1.333 1.333 1.333h6c.736 0 1.334-.598 1.334-1.333v-6c0-.736-.598-1.334-1.334-1.334z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M3.333 10.667h-.666a1.333 1.333 0 01-1.334-1.334v-6a1.333 1.333 0 011.334-1.333h6c.735 0 1.333.598 1.333 1.333v.667"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-all"
              style={{
                padding: "6px",
                color: "#9ca3af",
                backgroundColor: "transparent",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.target.style.color = "#6b7280";
                e.target.style.backgroundColor = "#f3f4f6";
              }}
              onMouseLeave={(e) => {
                e.target.style.color = "#9ca3af";
                e.target.style.backgroundColor = "transparent";
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.667 5.333L14 8m0 0l-2.333 2.667M14 8H6m-2.667 6h-1C1.597 14 1 13.403 1 12.667V3.333C1 2.597 1.597 2 2.333 2h1"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            {contract.name && (
              <span className="ml-4 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                {contract.name}
              </span>
            )}
          </div>
        </div>

        {/* Overview Section */}
        <div
          className="bg-gray-50 px-6 py-6 grid grid-cols-1 md:grid-cols-3 gap-6"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "24px",
            padding: "24px",
            backgroundColor: "#f9fafb",
          }}
        >
          <div
            className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm"
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "24px",
              border: "1px solid #e5e7eb",
              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
            }}
          >
            <h3
              className="text-lg font-semibold text-gray-900 mb-4"
              style={{
                fontSize: "18px",
                fontWeight: "600",
                color: "#111827",
                marginBottom: "16px",
              }}
            >
              Overview
            </h3>

            <div
              className="space-y-4"
              style={{ display: "flex", flexDirection: "column", gap: "16px" }}
            >
              {/* Total Supply display removed */}
              {/* <div
                className="flex justify-between items-center"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span className="text-gray-600" style={{ color: "#6b7280" }}>
                  Total Supply
                </span>
                <span
                  className="text-gray-900 font-medium"
                  style={{ color: "#111827", fontWeight: "500" }}
                >
                  {contract.totalSupply || contract.total_supply || "0"}
                </span>
              </div> */}

              <div
                className="flex justify-between items-center"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span className="text-gray-600" style={{ color: "#6b7280" }}>
                  Transaction Count
                </span>
                <span
                  className="text-gray-900 font-medium"
                  style={{ color: "#111827", fontWeight: "500" }}
                >
                  {contract.transactionCount || 0}
                </span>
              </div>
            </div>
          </div>

          <div
            className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm"
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "24px",
              border: "1px solid #e5e7eb",
              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
            }}
          >
            <h3
              className="text-lg font-semibold text-gray-900 mb-4"
              style={{
                fontSize: "18px",
                fontWeight: "600",
                color: "#111827",
                marginBottom: "16px",
              }}
            >
              More info
            </h3>

            <div
              className="space-y-4"
              style={{ display: "flex", flexDirection: "column", gap: "16px" }}
            >
              <div>
                <span
                  className="text-gray-600 text-sm"
                  style={{ color: "#6b7280", fontSize: "14px" }}
                >
                  Creator
                </span>
                <div
                  className="mt-1 flex items-center space-x-2"
                  style={{
                    marginTop: "4px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <a
                    href={`/accounts/${contract.creator}`}
                    className="font-mono text-sm"
                    style={{
                      color: "#00868A",
                      fontFamily: "Overpass Mono, monospace",
                      fontSize: "14px",
                      textDecoration: "none",
                      transition: "color 0.2s ease",
                    }}
                    onMouseEnter={(e) => (e.target.style.color = "#00A786")}
                    onMouseLeave={(e) => (e.target.style.color = "#00868A")}
                  >
                    {formatAddress(contract.creator)}
                  </a>
                  <button
                    onClick={() => copyToClipboard(contract.creator)}
                    className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                    style={{
                      padding: "4px",
                      color: "#9ca3af",
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                    }}
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M13.333 5.333h-6c-.735 0-1.333.598-1.333 1.334v6c0 .735.598 1.333 1.333 1.333h6c.736 0 1.334-.598 1.334-1.333v-6c0-.736-.598-1.334-1.334-1.334z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M3.333 10.667h-.666a1.333 1.333 0 01-1.334-1.334v-6a1.333 1.333 0 011.334-1.333h6c.735 0 1.333.598 1.333 1.333v.667"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              <div>
                <span
                  className="text-gray-600 text-sm"
                  style={{ color: "#6b7280", fontSize: "14px" }}
                >
                  Created at
                </span>
                <div className="mt-1" style={{ marginTop: "4px" }}>
                  <a
                    href={`/blocks/${contract.createdAtBlock}`}
                    className="text-sm"
                    style={{
                      color: "#00868A",
                      fontSize: "14px",
                      textDecoration: "none",
                      transition: "color 0.2s ease",
                    }}
                    onMouseEnter={(e) => (e.target.style.color = "#00A786")}
                    onMouseLeave={(e) => (e.target.style.color = "#00868A")}
                  >
                    Block #{contract.createdAtBlock}
                  </a>
                  <span
                    className="text-gray-500 text-sm ml-2"
                    style={{
                      color: "#6b7280",
                      fontSize: "14px",
                      marginLeft: "8px",
                    }}
                  >
                    (
                    {contract.createdAt
                      ? new Date(contract.createdAt).toLocaleDateString()
                      : "Unknown"}
                    )
                  </span>
                </div>
              </div>

              <div
                className="flex justify-between items-center"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span
                  className="text-gray-600 text-sm"
                  style={{ color: "#6b7280", fontSize: "14px" }}
                >
                  ABI Available
                </span>
                <span
                  className={`px-2 py-1 rounded text-xs font-medium ${
                    contract.abi || actualAbi
                      ? "bg-green-100 text-green-700 border border-green-200"
                      : "bg-gray-100 text-gray-600 border border-gray-200"
                  }`}
                  style={{
                    padding: "4px 8px",
                    borderRadius: "4px",
                    fontSize: "12px",
                    fontWeight: "500",
                    backgroundColor:
                      contract.abi || actualAbi ? "#dcfce7" : "#f3f4f6",
                    color: contract.abi || actualAbi ? "#15803d" : "#6b7280",
                    border:
                      contract.abi || actualAbi
                        ? "1px solid #bbf7d0"
                        : "1px solid #e5e7eb",
                  }}
                >
                  {contract.abi || actualAbi ? "TRUE" : "FALSE"}
                </span>
              </div>

              {/* Total Supply */}
              <div
                className="flex justify-between items-center"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: "16px",
                }}
              >
                <span
                  className="text-gray-600 text-sm"
                  style={{ color: "#6b7280", fontSize: "14px" }}
                >
                  Total Supply
                </span>
                <span
                  className="font-mono text-sm"
                  style={{
                    fontFamily: "Overpass Mono, monospace",
                    fontSize: "14px",
                    color: "#091D39",
                    fontWeight: "500",
                  }}
                >
                  1
                </span>
              </div>
            </div>
          </div>

          <div
            className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm"
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "24px",
              border: "1px solid #e5e7eb",
              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
            }}
          >
            <h3
              className="text-lg font-semibold text-gray-900 mb-4"
              style={{
                fontSize: "18px",
                fontWeight: "600",
                color: "#111827",
                marginBottom: "16px",
              }}
            >
              Details
            </h3>

            <div
              className="space-y-4"
              style={{ display: "flex", flexDirection: "column", gap: "16px" }}
            >
              {contract.symbol && (
                <div
                  className="flex justify-between items-center"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span className="text-gray-600" style={{ color: "#6b7280" }}>
                    Symbol
                  </span>
                  <span
                    className="text-gray-900 font-medium"
                    style={{ color: "#111827", fontWeight: "500" }}
                  >
                    {contract.symbol}
                  </span>
                </div>
              )}

              {contract.codeHash && (
                <div>
                  <span
                    className="text-gray-600 text-sm"
                    style={{ color: "#6b7280", fontSize: "14px" }}
                  >
                    Code Hash
                  </span>
                  <div
                    className="mt-1 flex items-center space-x-2"
                    style={{
                      marginTop: "4px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span
                      className="text-gray-900 font-mono text-sm"
                      style={{
                        color: "#111827",
                        fontFamily: "Overpass Mono, monospace",
                        fontSize: "14px",
                      }}
                    >
                      {formatAddress(contract.codeHash)}
                    </span>
                    <button
                      onClick={() => copyToClipboard(contract.codeHash)}
                      className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                      style={{
                        padding: "4px",
                        color: "#9ca3af",
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                      }}
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M13.333 5.333h-6c-.735 0-1.333.598-1.333 1.334v6c0 .735.598 1.333 1.333 1.333h6c.736 0 1.334-.598 1.334-1.333v-6c0-.736-.598-1.334-1.334-1.334z"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M3.333 10.667h-.666a1.333 1.333 0 01-1.334-1.334v-6a1.333 1.333 0 011.334-1.333h6c.735 0 1.333.598 1.333 1.333v.667"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              )}

              {contract.extrinsicHash && (
                <div>
                  <span
                    className="text-gray-600 text-sm"
                    style={{ color: "#6b7280", fontSize: "14px" }}
                  >
                    Creation Tx
                  </span>
                  <div
                    className="mt-1 flex items-center space-x-2"
                    style={{
                      marginTop: "4px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <a
                      href={`/extrinsics/${contract.extrinsicHash}`}
                      className="font-mono text-sm"
                      style={{
                        color: "#00868A",
                        fontFamily: "Overpass Mono, monospace",
                        fontSize: "14px",
                        textDecoration: "none",
                        transition: "color 0.2s ease",
                      }}
                      onMouseEnter={(e) => (e.target.style.color = "#00A786")}
                      onMouseLeave={(e) => (e.target.style.color = "#00868A")}
                    >
                      {formatAddress(contract.extrinsicHash)}
                    </a>
                    <button
                      onClick={() => copyToClipboard(contract.extrinsicHash)}
                      className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                      style={{
                        padding: "4px",
                        color: "#9ca3af",
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                      }}
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M13.333 5.333h-6c-.735 0-1.333.598-1.333 1.334v6c0 .735.598 1.333 1.333 1.333h6c.736 0 1.334-.598 1.334-1.333v-6c0-.736-.598-1.334-1.334-1.334z"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M3.333 10.667h-.666a1.333 1.333 0 01-1.334-1.334v-6a1.333 1.333 0 011.334-1.333h6c.735 0 1.333.598 1.333 1.333v.667"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              )}

              {/* Token Standard - Hardcoded */}
              <div>
                <span
                  className="text-gray-600 text-sm"
                  style={{ color: "#6b7280", fontSize: "14px" }}
                >
                  Token Standard
                </span>
                <div className="mt-1" style={{ marginTop: "4px" }}>
                  <span
                    className="font-mono text-sm"
                    style={{
                      fontFamily: "Overpass Mono, monospace",
                      fontSize: "14px",
                      color: "#091D39",
                      fontWeight: "500",
                    }}
                  >
                    REGA-1-20
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs Section */}
        <div className="bg-white" style={{ backgroundColor: "white" }}>
          <div
            className="border-b border-gray-200"
            style={{ borderBottom: "1px solid #e5e7eb" }}
          >
            <nav
              className="flex px-6"
              style={{
                display: "flex",
                paddingLeft: "24px",
                paddingRight: "24px",
              }}
            >
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-6 py-4 font-medium text-sm transition-all relative ${
                    activeTab === tab.id
                      ? "text-blue-600 border-b-2 border-blue-600"
                      : "text-gray-600 hover:text-gray-900 border-b-2 border-transparent"
                  }`}
                  style={{
                    padding: "16px 24px",
                    fontWeight: "500",
                    fontSize: "14px",
                    background: "none",
                    border: "none",
                    borderBottom:
                      activeTab === tab.id
                        ? "2px solid #00868A"
                        : "2px solid transparent",
                    color: activeTab === tab.id ? "#00868A" : "#6b7280",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    position: "relative",
                  }}
                  onMouseEnter={(e) => {
                    if (activeTab !== tab.id) {
                      e.target.style.color = "#11545F";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeTab !== tab.id) {
                      e.target.style.color = "#6b7280";
                    }
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div
            className="p-6"
            style={{
              padding: "24px",
              backgroundColor: "white",
            }}
          >
            {activeTab === "transactions" && (
              <ContractCalls
                calls={transactions}
                decimals={decimals}
                symbol={symbol}
              />
            )}
            {activeTab === "events" && (
              <ContractEvents
                events={events}
                decimals={decimals}
                symbol={symbol}
              />
            )}
            {/* Read/Write tab content - COMMENTED OUT */}
            {/* {activeTab === "readwrite" && (
              <ContractInteraction
                contract={contract}
                address={address}
                onAbiChange={setActualAbi}
              />
            )} */}
            {activeTab === "holders" && (
              <TokenHolders
                contract={{
                  ...contract,
                  events: contract.events || events,
                  abi: contract.abi || actualAbi,
                }}
                address={address}
              />
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
